import { useEffect, useRef } from 'react';
import { useClickAway } from 'react-use';

import { Portal } from 'components/Portal';

import * as S from './Modal.styles';

type ModalProps = {
  children: React.ReactNode;
  onClose?: () => void;
  hasLightOpacity?: boolean;
  maxWidth?: string;
};

const defaultProps: Partial<ModalProps> = {};

export function Modal(props: ModalProps): React.ReactElement {
  const { children, onClose, hasLightOpacity, maxWidth } = { ...defaultProps, ...props };

  const ref = useRef(null);

  useClickAway(ref, onClose!);

  // react-use's useLockBodyScroll just toggles `overflow: hidden` on <body>,
  // which removes the scrollbar and widens the viewport by its width —
  // every fixed-width element on the page (including this modal's own
  // trigger button) visibly jumps sideways for a frame. Compensating with
  // padding-right keeps the page width stable while the modal is open.
  useEffect(() => {
    const { body } = document;
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    const previousOverflow = body.style.overflow;
    const previousPaddingRight = body.style.paddingRight;

    body.style.overflow = 'hidden';
    if (scrollBarWidth > 0) {
      body.style.paddingRight = `${scrollBarWidth}px`;
    }

    return () => {
      body.style.overflow = previousOverflow;
      body.style.paddingRight = previousPaddingRight;
    };
  }, []);

  // Applies to every Modal-based popup in the app (DeleteModal, FormModal,
  // SuccessModal, DetailsModal) — Escape closes it just like clicking away.
  useEffect(() => {
    if (!onClose) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <Portal>
      <S.Container hasLightOpacity={hasLightOpacity}>
        <S.Box ref={ref} maxWidth={maxWidth}>
          <S.BoxInner>
            {onClose ? <S.CloseButton onClick={onClose} /> : null}

            {children}
          </S.BoxInner>
        </S.Box>
      </S.Container>
    </Portal>
  );
}
